import { useState } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";

import MDInput from "components/MDInput";
import ActionDialog from "components/admin/ActionDialog";
import TemplatePicker from "components/admin/TemplatePicker";
import { GEM_DUST_ID, GEM_ITEM_TYPE, MAX_GEM_LEVEL } from "components/admin/GiftEditor";
import { useRunner } from "components/admin/hooks";
import api from "api/client";
import { formatNumber } from "utils/format";

export const dialogProps = {
  userId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Gọi API rồi đóng hộp thoại và tải lại trang chi tiết nếu thành công. */
export function useSubmit(onClose, onDone) {
  const [run, busy] = useRunner();
  const submit = async (action) => {
    const { ok } = await run(action, (data) => (typeof data === "string" ? data : "Thành công"));
    if (ok) {
      onClose();
      onDone();
    }
  };
  return [submit, busy];
}

export function BanDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [permanent, setPermanent] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("");
  return (
    <ActionDialog
      open
      title="Khoá tài khoản"
      description="Chặn tài khoản này đăng nhập vào game trong số phút đã chọn hoặc vĩnh viễn. Gỡ khoá bằng nút Gỡ khoá."
      color="error"
      submitText="Khoá"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/ban`, {
            permanent,
            minutes: Number(minutes),
            reason,
          })
        )
      }
    >
      <FormControlLabel
        control={<Switch checked={permanent} onChange={(e) => setPermanent(e.target.checked)} />}
        label="Khoá vĩnh viễn"
      />
      {!permanent && (
        <MDInput
          type="number"
          label="Số phút khoá (1440 = 1 ngày)"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
        />
      )}
      <MDInput
        label="Lý do"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        minRows={2}
      />
      <Alert severity="info">Người chơi đang online sẽ bị ngắt kết nối ngay.</Alert>
    </ActionDialog>
  );
}
BanDialog.propTypes = dialogProps;

const MUTE_PRESETS = [
  { value: 60, label: "1 giờ" },
  { value: 1440, label: "1 ngày" },
  { value: 10080, label: "7 ngày" },
  { value: 43200, label: "30 ngày" },
];

export function MuteDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("");
  return (
    <ActionDialog
      open
      title="Khoá chat"
      description="Cấm người chơi chat trong khoảng thời gian chọn; hết giờ tự mở lại."
      color="warning"
      submitText="Khoá chat"
      busy={busy}
      disabled={Number(minutes) <= 0}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/mute`, { minutes: Number(minutes), reason })
        )
      }
    >
      <MDInput
        select
        label="Nhanh"
        value={MUTE_PRESETS.some((p) => p.value === Number(minutes)) ? Number(minutes) : ""}
        onChange={(e) => setMinutes(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        {MUTE_PRESETS.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
      </MDInput>
      <MDInput
        type="number"
        label="Số phút khoá chat (1440 = 1 ngày)"
        value={minutes}
        onChange={(e) => setMinutes(e.target.value)}
      />
      <MDInput
        label="Lý do (người chơi sẽ thấy)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        minRows={2}
      />
      <Alert severity="info">
        Chặn chat khu vực, thế giới, bang hội và tin nhắn riêng. Người chơi vẫn vào game và chơi
        bình thường.
      </Alert>
    </ActionDialog>
  );
}
MuteDialog.propTypes = dialogProps;

const CURRENCIES = [
  { value: "gold", label: "Vàng" },
  { value: "coin", label: "Ngọc" },
  { value: "lua", label: "Lúa" },
  { value: "star", label: "Năng lượng" },
];

export function CurrencyDialog({ userId, onClose, onDone, current }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [type, setType] = useState("gold");
  const [subtract, setSubtract] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const value = Math.abs(Number(amount) || 0);
  return (
    <ActionDialog
      open
      title="Cộng / trừ tiền tệ"
      description="Thay đổi trực tiếp số vàng, ngọc, lúa, năng lượng của nhân vật. Pet Coin (tiền nạp) cộng / trừ ở nút Pet Coin riêng. Mỗi lần đổi đều ghi vào lịch sử kèm lý do."
      color={subtract ? "error" : "success"}
      submitText={subtract ? "Trừ" : "Cộng"}
      busy={busy}
      disabled={value <= 0}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/currency`, {
            type,
            amount: subtract ? -value : value,
            reason,
          })
        )
      }
    >
      <MDInput
        select
        label="Loại"
        value={type}
        onChange={(e) => setType(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        {CURRENCIES.map((c) => (
          <MenuItem key={c.value} value={c.value}>
            {c.label}{" "}
            {current[c.value] !== undefined ? `(hiện có ${formatNumber(current[c.value])})` : ""}
          </MenuItem>
        ))}
      </MDInput>
      <FormControlLabel
        control={<Switch checked={subtract} onChange={(e) => setSubtract(e.target.checked)} />}
        label="Trừ thay vì cộng"
      />
      <MDInput
        type="number"
        label="Số lượng"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <MDInput
        label="Lý do (ghi vào lịch sử)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </ActionDialog>
  );
}
CurrencyDialog.propTypes = {
  ...dialogProps,
  current: PropTypes.objectOf(PropTypes.number).isRequired,
};

export function ItemDialog({ userId, onClose, onDone, isAdmin }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [itemTemplateId, setItemTemplateId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [count, setCount] = useState(1);
  const [maxOption, setMaxOption] = useState(false);
  const [gemLevel, setGemLevel] = useState(0);
  const [noTrade, setNoTrade] = useState(false);
  const [title, setTitle] = useState("Quà từ Admin");
  const [content, setContent] = useState("");
  const isGem = template?.type === GEM_ITEM_TYPE;
  const isGemDust = itemTemplateId === GEM_DUST_ID;
  return (
    <ActionDialog
      open
      title="Tặng vật phẩm"
      description="Tạo vật phẩm mới và gửi qua hòm thư để người chơi tự nhận."
      submitText="Tặng"
      busy={busy}
      disabled={!itemTemplateId}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/items`, {
            itemTemplateId,
            count: Number(count),
            maxOption: !isGem && maxOption,
            gemLevel: isGem ? Number(gemLevel) : 0,
            noTrade: isGem && noTrade,
            title,
            content,
          })
        )
      }
    >
      <TemplatePicker
        kind="item"
        label="Vật phẩm"
        value={itemTemplateId}
        hideAdminItems={!isAdmin}
        onChange={(id, option) => {
          setItemTemplateId(id);
          setTemplate(option);
        }}
      />
      <MDInput
        type="number"
        label={template && !template.isStackable ? "Số lượng (tối đa 100 món)" : "Số lượng"}
        value={count}
        onChange={(e) => setCount(e.target.value)}
      />
      {template && !template.isStackable && !isGem && (
        <FormControlLabel
          control={<Switch checked={maxOption} onChange={(e) => setMaxOption(e.target.checked)} />}
          label="Chỉ số tối đa"
        />
      )}
      {isGemDust && (
        <Alert severity="info">
          Bụi ngọc luôn khoá giao dịch. Đổi 1 ngọc bậc 1: 30 bụi (hệ thường), 100 bụi (bóng tối /
          ánh sáng).
        </Alert>
      )}
      {isGem && (
        <>
          <MDInput
            type="number"
            label={`Cấp cường hoá ngọc (0 - ${MAX_GEM_LEVEL})`}
            value={gemLevel}
            inputProps={{ min: 0, max: MAX_GEM_LEVEL }}
            onChange={(e) => setGemLevel(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={noTrade} onChange={(e) => setNoTrade(e.target.checked)} />}
            label="Không cho giao dịch"
          />
        </>
      )}
      <MDInput label="Tiêu đề thư" value={title} onChange={(e) => setTitle(e.target.value)} />
      <MDInput
        label="Nội dung thư"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        multiline
        minRows={2}
      />
    </ActionDialog>
  );
}
ItemDialog.propTypes = {
  ...dialogProps,
  isAdmin: PropTypes.bool.isRequired,
};

export function PetDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [petTemplateId, setPetTemplateId] = useState(null);
  const [count, setCount] = useState(1);
  const [expireDays, setExpireDays] = useState(0);
  return (
    <ActionDialog
      open
      title="Tặng pet"
      description="Tạo pet mới cho người chơi, có thể đặt hạn dùng. Pet được gửi qua hòm thư để người chơi tự nhận."
      submitText="Tặng"
      busy={busy}
      disabled={!petTemplateId}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/pets`, {
            petTemplateId,
            count: Number(count),
            expireDays: Number(expireDays),
          })
        )
      }
    >
      <TemplatePicker kind="pet" label="Pet" value={petTemplateId} onChange={setPetTemplateId} />
      <MDInput
        type="number"
        label="Số lượng (1 - 10)"
        value={count}
        onChange={(e) => setCount(e.target.value)}
      />
      <MDInput
        type="number"
        label="Hạn dùng (ngày, 0 = vĩnh viễn)"
        value={expireDays}
        onChange={(e) => setExpireDays(e.target.value)}
      />
    </ActionDialog>
  );
}
PetDialog.propTypes = { ...dialogProps };

export function BuffItemDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [form, setForm] = useState({
    itemTemplateId: null,
    count: 1,
    endLevel: 10,
    levelUpTier: 0,
    maxTier: 0,
    numFusion: 0,
    maxOption: true,
  });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  return (
    <ActionDialog
      open
      title="Buff trang bị (đập đồ nhanh)"
      description="Tạo sẵn trang bị đã cường hoá, tiến hoá và dung hợp tới mức chọn rồi đưa vào hành trang, thay vì phải đập từng lần trong game."
      color="warning"
      submitText="Tạo trang bị"
      busy={busy}
      disabled={!form.itemTemplateId}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/buff-item`, {
            itemTemplateId: form.itemTemplateId,
            count: Number(form.count),
            endLevel: Number(form.endLevel),
            levelUpTier: Number(form.levelUpTier),
            maxTier: Number(form.maxTier),
            numFusion: Number(form.numFusion),
            maxOption: form.maxOption,
          })
        )
      }
    >
      <Alert severity="warning">
        Chỉ dùng cho trang bị, người chơi phải đang offline. Trang bị vào hành trang và người chơi
        thấy ở lần đăng nhập tới.
      </Alert>
      <TemplatePicker
        kind="item"
        label="Trang bị"
        value={form.itemTemplateId}
        onChange={(id) => setForm({ ...form, itemTemplateId: id })}
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lượng (1 - 20)"
            value={form.count}
            onChange={set("count")}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Cấp cường hoá cuối"
            value={form.endLevel}
            onChange={set("endLevel")}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Cấp đập trước mỗi lần tiến hoá"
            value={form.levelUpTier}
            onChange={set("levelUpTier")}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số bậc tiến hoá"
            value={form.maxTier}
            onChange={set("maxTier")}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lần dung hợp"
            value={form.numFusion}
            onChange={set("numFusion")}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Switch
                checked={form.maxOption}
                onChange={(e) => setForm({ ...form, maxOption: e.target.checked })}
              />
            }
            label="Chỉ số tối đa"
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}
BuffItemDialog.propTypes = dialogProps;

const PASSWORD_CHARS = "abcdefghijkmnpqrstuvwxyz23456789";

function randomPassword(length = 10) {
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (v) => PASSWORD_CHARS[v % PASSWORD_CHARS.length]).join("");
}

export function PasswordDialog({ userId, username, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(true);
  const invalid = password.length < 6 || password.length >= 60 || /\s/.test(password);
  return (
    <ActionDialog
      open
      title={`Đặt mật khẩu mới cho ${username}`}
      color="warning"
      submitText="Đổi mật khẩu"
      busy={busy}
      disabled={invalid}
      onClose={onClose}
      onSubmit={() => submit(() => api.post(`/api/admin/players/${userId}/password`, { password }))}
    >
      <Alert severity="info">
        Mật khẩu được mã hoá một chiều (bcrypt) nên không ai xem lại được mật khẩu cũ, kể cả admin.
        Hãy đặt mật khẩu mới rồi gửi cho người chơi.
      </Alert>
      <MDInput
        label="Mật khẩu mới (6 - 59 ký tự, không có khoảng trắng)"
        type={visible ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Tạo ngẫu nhiên">
                <IconButton size="small" onClick={() => setPassword(randomPassword())}>
                  <Icon>casino</Icon>
                </IconButton>
              </Tooltip>
              <Tooltip title="Sao chép">
                <span>
                  <IconButton
                    size="small"
                    disabled={!password}
                    onClick={() => navigator.clipboard?.writeText(password)}
                  >
                    <Icon>content_copy</Icon>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={visible ? "Ẩn" : "Hiện"}>
                <IconButton size="small" onClick={() => setVisible(!visible)}>
                  <Icon>{visible ? "visibility_off" : "visibility"}</Icon>
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
    </ActionDialog>
  );
}
PasswordDialog.propTypes = { ...dialogProps, username: PropTypes.string.isRequired };

const inlineRadioSx = {
  display: "flex",
  alignItems: "center",
  "& .MuiFormControlLabel-label": { lineHeight: 1.4, transform: "none" },
};

export function DeletePlayerDialog({ userId, name, username, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [deleteAccount, setDeleteAccount] = useState(!name);
  const [confirmText, setConfirmText] = useState("");
  const expected = name || username;
  let title = `Xoá tài khoản ${username}`;
  if (name) {
    title = deleteAccount
      ? `Xoá tài khoản ${username} và nhân vật ${name}`
      : `Xoá nhân vật ${name}`;
  }
  return (
    <ActionDialog
      open
      title={title}
      color="error"
      submitText={deleteAccount ? "Xoá tài khoản" : "Xoá nhân vật"}
      busy={busy}
      disabled={confirmText.trim() !== expected}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/delete`, { deleteAccount, confirm: confirmText })
        )
      }
    >
      <RadioGroup
        value={deleteAccount ? "account" : "player"}
        onChange={(e) => setDeleteAccount(e.target.value === "account")}
      >
        <FormControlLabel
          value="player"
          disabled={!name}
          control={<Radio />}
          sx={inlineRadioSx}
          label={
            name
              ? `Chỉ xoá nhân vật ${name}, giữ tài khoản ${username} (đăng nhập lại sẽ tạo nhân vật mới)`
              : "Tài khoản chưa có nhân vật"
          }
        />
        <FormControlLabel
          value="account"
          control={<Radio />}
          sx={inlineRadioSx}
          label={`Xoá tài khoản ${username}${
            name ? " cùng nhân vật" : ""
          } (không đăng nhập lại được nữa)`}
        />
      </RadioGroup>
      <Alert severity="error">
        {deleteAccount ? "Xoá tài khoản web, nhân vật" : "Xoá nhân vật"} cùng hành trang, pet, hòm
        thư, đồ đang treo ki ốt, điểm săn boss và danh sách bạn bè. Lịch sử, log boss và giao dịch
        nạp tiền vẫn được giữ lại. Bản sao dữ liệu bị xoá được lưu vào
        GServer/backup_deleted_players.
      </Alert>
      <MDInput
        label={`Gõ "${expected}" để xác nhận`}
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        autoFocus
        autoComplete="off"
      />
      <Alert severity="info">
        Người chơi phải offline (kick trước nếu đang online). Không xoá được nhân vật admin hoặc
        bang chủ.
      </Alert>
    </ActionDialog>
  );
}
DeletePlayerDialog.defaultProps = { name: null };
DeletePlayerDialog.propTypes = {
  ...dialogProps,
  name: PropTypes.string,
  username: PropTypes.string.isRequired,
};

export function MessageDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [kind, setKind] = useState("ok");
  const [text, setText] = useState("");
  return (
    <ActionDialog
      open
      title="Gửi thông báo riêng"
      description="Hiện một thông báo trên màn hình của riêng người chơi này (phải đang online). Không lưu lại như thư."
      submitText="Gửi"
      busy={busy}
      disabled={!text.trim()}
      onClose={onClose}
      onSubmit={() =>
        submit(() => api.post(`/api/admin/players/${userId}/message`, { kind, text }))
      }
    >
      <MDInput
        select
        label="Kiểu"
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        <MenuItem value="ok">Hộp thoại</MenuItem>
        <MenuItem value="red">Hộp thoại đỏ (cảnh báo)</MenuItem>
        <MenuItem value="popup">Thông báo nhỏ</MenuItem>
      </MDInput>
      <MDInput
        label="Nội dung"
        value={text}
        onChange={(e) => setText(e.target.value)}
        multiline
        minRows={3}
        autoFocus
      />
    </ActionDialog>
  );
}
MessageDialog.propTypes = dialogProps;
